import React from 'react';
import { StyleSheet, View } from 'react-native';

import { handleAuthError } from '@auth/AuthErrorHandler';
import { validateUserEmail } from '@auth/EmailAndPassword';
import { sendPasswordResetEmail } from '@auth/ManageUser';
import { createStringValidator, submitFormSafely, useAppForm } from '@components/Form';
import { BackButton } from '@components/IconButton';
import ScreenContainer from '@components/ScreenContainer';
import { Caption, Title } from '@components/Typography';
import { AuthRoute } from '@navigation/Routes';
import { ResetPasswordScreenProps } from '@navigation/Types';

type ResetPasswordFormValues = {
  email: string;
};

export default function ResetPasswordScreen({ navigation }: ResetPasswordScreenProps) {
  const form = useAppForm({
    defaultValues: {
      email: '',
    } satisfies ResetPasswordFormValues,
    onSubmit: async ({ value }) => {
      try {
        await sendPasswordResetEmail(value.email);
        navigation.navigate(AuthRoute.Login);
      } catch (error) {
        handleAuthError(error);
        throw error;
      }
    },
  });

  const onResetPasswordPressed = () => submitFormSafely(form);

  const renderIntro = () => (
    <>
      <Title style={styles.headline}>Reset Password</Title>
      <Caption style={styles.caption}>
        Enter your account email address to receive a password reset link.
      </Caption>
    </>
  );

  const renderEmailInput = () => (
    <form.AppField name="email" validators={{ onChange: createStringValidator(validateUserEmail) }}>
      {(field) => (
        <field.EmailInput
          label="Email"
          returnKeyType="done"
          onSubmitEditing={onResetPasswordPressed}
          testID="screens.ResetPasswordScreen.TextInput.Email"
        />
      )}
    </form.AppField>
  );

  return (
    <form.AppForm>
      <ScreenContainer scrollable testID="screens.ResetPasswordScreen.Background">
        <BackButton onPress={navigation.goBack} testID="screens.ResetPasswordScreen.BackButton" />
        <View style={styles.container}>
          {renderIntro()}
          {renderEmailInput()}
          <form.SubmitBigButton
            mode="contained"
            testID="screens.ResetPasswordScreen.Button.ResetPassword">
            Submit
          </form.SubmitBigButton>
        </View>
      </ScreenContainer>
    </form.AppForm>
  );
}

const styles = StyleSheet.create({
  caption: {
    alignSelf: 'center',
    paddingHorizontal: 5,
  },
  container: {
    paddingHorizontal: 20,
  },
  headline: {
    textAlign: 'center',
  },
});
